/**
 * Sentry Error Tracking Setup
 *
 * Initializes Sentry for error tracking in non-development environments.
 * Includes filtering for known non-critical errors and user context.
 *
 * @see ADR-058: Architecture Cleanup
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import { environment } from '../environments/environment';

/**
 * Initialize Sentry error tracking
 *
 * Skips initialization in local development unless sentryDebug is enabled.
 * Configures:
 * - Browser tracing for performance monitoring
 * - Session replay in production/staging
 * - Error filtering for known non-critical issues
 * - User context from Redux store
 */
export function initializeSentry(): void {
  const sentryEnv = window.__ENV?.NX_SENTRY_ENVIROMENT || window.__ENV?.NX_SENTRY_ENVIRONMENT || environment.sentryEnvironment;
  const isLocalHost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.endsWith('.localhost')
  );
  // On a local host only an explicit production or staging build reports; any
  // other environment name, including a stale `develop`, is suppressed.
  const isLocalDev =
    sentryEnv === 'local' ||
    (isLocalHost && sentryEnv !== 'production' && sentryEnv !== 'staging');

  // Skip local development unless sentryDebug is enabled
  if (isLocalDev && !environment.sentryDebug) {
    return;
  }
  Sentry.init({
    dsn: window.__ENV?.NX_SENTRY_DSN || environment.sentryDsn,
    integrations: [
      // React Router v7 instrumentation for better transaction naming
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Enable replay only in production/staging
      ...(sentryEnv === 'production' ||
        sentryEnv === 'staging'
        ? [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ]
        : []),
    ],

    tracesSampleRate: environment.sentryTracesSampleRate,
    tracePropagationTargets: [/^https:\/\/([a-zA-Z0-9-]+\.)?aegix\.global(\/|$)/],
    replaysSessionSampleRate: environment.sentryReplaysSessionSampleRate,
    replaysOnErrorSampleRate: 1.0,
    environment: sentryEnv,
    release: process.env['SENTRY_RELEASE'],

    beforeSend(event, hint) {
      // Skip errors in local development unless sentryDebug is enabled
      if (isLocalDev && !environment.sentryDebug) {
        return null;
      }

      // Drop events originating from localhost dev servers — never user-visible.
      const eventUrl = event.request?.url || '';
      if (/^https?:\/\/(?:[a-z0-9-]+\.)*(?:localhost|127\.0\.0\.1)(?::\d+)?(?:\/|$)/i.test(eventUrl)) {
        return null;
      }

      // Drop Vite HMR / React Fast Refresh transient errors (e.g. "React is not defined"
      // thrown from @react-refresh during an HMR cycle). These can only occur in `vite dev`.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      if (frames.some((f) => f.filename?.includes('@react-refresh'))) {
        return null;
      }

      const error = hint?.originalException;
      const errorMessage = event.exception?.values?.[0]?.value || '';

      // Filter out Vite dev-server artifacts (HMR/Fast Refresh transient errors).
      // These paths only exist at dev-server runtime and never appear in deployed builds.
      const viteDevPathPatterns = ['/@react-refresh', '/@vite/', '/@fs/', '/node_modules/.vite/'];
      const topFrame = event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
      if (topFrame?.filename && viteDevPathPatterns.some((p) => topFrame.filename!.includes(p))) {
        console.debug('Ignoring Vite dev artifact:', topFrame.filename);
        return null;
      }

      // Filter out known non-critical errors
      const ignoredErrors = [
        "This browser doesn't support the API's required to use the Firebase SDK",
        'Unable to reach any of the backend servers! Are you offline?',
        'Network Error',
        'ChunkLoadError',
        'Loading chunk',
        'Script error',
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        'Function components cannot have string refs',
        'The play() request was interrupted',
      ];

      if (ignoredErrors.some((ignored) => errorMessage.includes(ignored))) {
        console.debug('Ignoring known error:', errorMessage);
        return null;
      }

      // Filter out AbortError from media play() interruptions (vendored SDK, navigation timing)
      if (error instanceof DOMException && error.name === 'AbortError') {
        return null;
      }

      // Filter out HTTP-shaped errors (ky's HTTPError, ApiValidationError,
      // and any error that carries a `.response.status` from the data layer).
      // Only send 5xx server errors; ignore client/network/validation errors.
      // ADR 113: the codebase does not ship axios — check the response
      // structurally rather than via `axios.isAxiosError`.
      const httpLikeError = error as
        | { response?: { status?: number }; name?: string; message?: string }
        | null
        | undefined;
      const httpStatus = httpLikeError?.response?.status;
      if (
        typeof httpStatus === 'number' ||
        httpLikeError?.name === 'HTTPError' ||
        httpLikeError?.name === 'ApiValidationError'
      ) {
        if (typeof httpStatus === 'number' && httpStatus >= 500) {
          return event;
        }
        console.debug(
          'Filtering out HTTP error:',
          httpLikeError?.message ?? '<no message>',
        );
        return null;
      }

      // Filter out other common client-side non-critical errors
      if (
        error instanceof TypeError &&
        errorMessage.includes('Cannot read prop')
      ) {
        // Only send if it's in our code, not third-party
        const isOurCode =
          event.exception?.values?.[0]?.stacktrace?.frames?.some((frame) =>
            frame.filename?.includes('aegix-sms-react'),
          );
        if (!isOurCode) {
          console.debug('Ignoring third-party TypeError:', errorMessage);
          return null;
        }
      }

      // Add user context if available

      return event;
    },
  });
}
